import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Job } from '../lib/api';
import { useJobs } from '../components/JobToast';

interface Source {
  id: number;
  handle: string;
}

export function IngestPage() {
  const { trackJob } = useJobs();
  const [params] = useSearchParams();
  const [handle, setHandle] = useState(params.get('handle') ?? '');
  const [limit, setLimit] = useState(200);
  const [downloadMedia, setDownloadMedia] = useState(true);
  const [runOcr, setRunOcr] = useState(true);
  const [sources, setSources] = useState<Source[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ sources: Source[] }>('/api/sources').then((d) => setSources(d.sources));
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!handle.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { job } = await api.post<{ job: Job }>('/api/jobs/ingest', {
        handle: handle.trim(),
        limit,
        downloadMedia,
        runOcr,
      });
      const done = await trackJob(job.id, `Inladen ${handle.trim()}`);
      if (done.status === 'failed') {
        setError(done.error || 'Inladen mislukt');
      } else {
        const r = done.result as { inserted?: number; skipped?: number; fetched?: number; ocrCount?: number } | null;
        setResult(
          `${r?.inserted ?? 0} nieuw, ${r?.skipped ?? 0} overgeslagen (${r?.fetched ?? 0} opgehaald, ${r?.ocrCount ?? 0} OCR).`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inladen mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1>Kanaal inladen</h1>
      <p className="page-lead">
        Start een achtergrondjob. De website blijft volledig bruikbaar terwijl berichten worden opgehaald.
      </p>

      <form className="panel toolbar" onSubmit={(e) => void onSubmit(e)}>
        <div className="field" style={{ flex: 1 }}>
          <label>Kanaal</label>
          <input
            className="input"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="@kanaal"
            required
          />
        </div>
        <div className="field">
          <label>Aantal</label>
          <input
            className="input input-sm"
            type="number"
            min={1}
            max={2000}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          />
        </div>
        <label className="row" style={{ paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={downloadMedia}
            onChange={(e) => setDownloadMedia(e.target.checked)}
          />
          Media downloaden
        </label>
        <label className="row" style={{ paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={runOcr}
            onChange={(e) => setRunOcr(e.target.checked)}
          />
          OCR (NL+EN)
        </label>
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Gestart…' : 'Inladen'}
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}
      {result && <div className="alert ok">{result}</div>}

      {sources.length > 0 && (
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="muted" style={{ marginBottom: 10 }}>
            Bekende bronnen — klik om in te vullen
          </div>
          <div className="row">
            {sources.map((s) => (
              <button
                key={s.id}
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setHandle(s.handle)}
              >
                {s.handle}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
